import { labelBody, labelFooterRow, labelHeader } from '@/lib/labelLayout'
import { expectedScanCodes } from '@/lib/pickList'
import { lotScanCode } from '@/lib/lotScanCode'

/**
 * What an inventory lot label and a storage-location label SAY.
 *
 * How they look lives in `labelLayout.ts`, shared with the equipment tag so the
 * three read the same way round.
 *
 * THE SCAN VALUE COMES FROM `expectedScanCodes`, the same helper the pick list
 * verifies against. Never re-derive it. A label whose symbol says `LOC-A1S2B04`
 * while the verifier expects `LOC-A1-S2-B04` fails every scan on the floor, and
 * the mismatch is invisible until someone is at a shelf with a phone that will
 * not beep.
 *
 * ONE SYMBOL PER LABEL, AND IT IS THE LABEL'S OWN.
 *
 * An earlier draft put the location code on the lot label too, so one sticker
 * carried both scans. That quietly defeats the control it was meant to serve:
 * the pick list's two-step verification exists precisely BECAUSE the two codes
 * live on two different physical things — the bin on the rack and the box in
 * your hand. Print both on the box and a picker can satisfy the location scan
 * without ever looking at the shelf, which is the exact error the second scan is
 * there to catch. The lot label states where the lot lives as text; proving it
 * is the shelf label's job.
 */

export { LABEL_STYLE, labelSheetHtml } from '@/lib/labelLayout'

export interface LotLabelInput {
  materialCode: string
  materialName: string
  lotNumber: string
  expiryDate?: string | null
  quantity?: number | null
  zoneCode?: string | null
  locationCode?: string | null
}

export interface LocationLabelInput {
  locationCode: string
  zoneCode?: string | null
  zoneName?: string | null
}

/**
 * What a lot's symbol must encode.
 *
 * Re-exported from `lotScanCode.ts` rather than derived here, exactly as this
 * module's equipment sibling does: the printer, the pick-list verifier and the
 * mobile lookup's decoder all reach the one definition.
 */
export { lotScanCode }

/** What a bin's symbol must encode. */
export function locationScanCode(locationCode: string | null | undefined): string | null {
  return expectedScanCodes({
    locationCode: locationCode ?? null,
    materialCode: '',
    suggestedLotNumber: null,
  }).location
}

/** A lot label: what the lot is, and the symbol that proves this lot. */
export function lotLabelHtml(input: LotLabelInput): string {
  const item = lotScanCode({ materialCode: input.materialCode, lotNumber: input.lotNumber })
  const where = [input.zoneCode, input.locationCode].filter(Boolean).join(' · ')

  // Quantity and expiry ride in the header rather than as fields of their own.
  // They qualify the lot; they are not the reason anyone picked the label up.
  const meta = [
    input.quantity != null ? `Qty ${input.quantity}` : '',
    input.expiryDate ? `Exp ${input.expiryDate.slice(0, 10)}` : 'No expiry',
  ]
    .filter(Boolean)
    .join('  ·  ')

  return (
    `<div class="lbl">` +
    labelHeader('Lot', meta) +
    labelBody(item, input.lotNumber, `${input.materialCode} · ${input.materialName}`) +
    labelFooterRow('Location', where || 'Not homed') +
    `</div>`
  )
}

/** A shelf/bin label: the location code, and the symbol that proves the shelf. */
export function locationLabelHtml(input: LocationLabelInput): string {
  const location = locationScanCode(input.locationCode)
  return (
    `<div class="lbl">` +
    labelHeader('Location', input.zoneCode ?? '') +
    labelBody(location, input.locationCode, input.zoneName ?? '') +
    `</div>`
  )
}
